import { Trans, t } from "@lingui/macro";
import cx from "classnames";
import { useCallback, useMemo, useState } from "react";
import { Link as LinkOriginal } from "react-router-dom";
const Link = LinkOriginal as any;
import { type Address, getAddress, zeroAddress } from "viem";

import { getExplorerUrl } from "config/chains";
import { useMarketsInfoData } from "context/SyntheticsStateContext/hooks/globalsHooks";
import { selectAccount, selectChainId } from "context/SyntheticsStateContext/selectors/globalSelectors";
import { useSelector } from "context/SyntheticsStateContext/utils";
import { isSwapOrderType } from "domain/synthetics/orders";
import { convertToUsd } from "domain/synthetics/tokens";
import { PositionTradeAction, SwapTradeAction, TradeAction } from "domain/synthetics/tradeHistory";
import { getBasisPoints } from "lib/numbers";
import { EMPTY_ARRAY } from "lib/objects";
import { userAnalytics } from "lib/userAnalytics";
import { SharePositionClickEvent } from "lib/userAnalytics/types";
import { buildAccountDashboardUrl } from "pages/AccountDashboard/buildAccountDashboardUrl";
import { NETWORKS_BY_CHAIN_IDS } from "pages/ParseTransaction/ParseTransaction";

import PositionDropdown from "components/Exchange/PositionDropdown";
import PositionShare from "components/Exchange/PositionShare";
import ExternalLink from "components/ExternalLink/ExternalLink";
import Modal from "components/Modal/Modal";
import StatsTooltipRow from "components/StatsTooltip/StatsTooltipRow";
import { SwapMarketLabel } from "components/SwapMarketLabel/SwapMarketLabel";
import { TableTd, TableTr } from "components/Table/Table";
import TokenIcon from "components/TokenIcon/TokenIcon";
import TooltipWithPortal from "components/Tooltip/TooltipWithPortal";

import NewLink20ReactComponent from "img/ic_new_link_20.svg?react";

import { formatPositionMessage } from "./utils/position";
import { RowDetails, TooltipContent, TooltipString } from "./utils/shared";
import { formatSwapMessage } from "./utils/swap";

import "./TradeHistoryRow.scss";

type Props = {
  tradeAction: TradeAction;
  minCollateralUsd: bigint;
  shouldDisplayAccount?: boolean;
  showDebugValues?: boolean;
};

function LineSpan({ span }: { span: TooltipString }) {
  if (span === undefined) {
    return null;
  }

  if (typeof span === "string") {
    return <span>{span}</span>;
  }

  return (
    <span
      className={cx({
        "text-red-alpha-100": span.state === "error",
        "text-primary-alpha-darker": span.state === "success",
        muted: span.state === "muted",
      })}
    >
      {span.text}
    </span>
  );
}

function LineSpans({ spans }: { spans: TooltipString[] }) {
  return (
    <>
      {spans.map((span, i) => (
        <LineSpan key={i} span={span} />
      ))}
    </>
  );
}

function TooltipContentComponent({ content }: { content: TooltipContent }) {
  return (
    <div className="TradeHistoryRow-tooltip">
      {content.map((line, i) => {
        if (line === undefined) {
          return null;
        }

        if (line === "") {
          return <br key={i} />;
        }

        if (typeof line === "string") {
          return <div key={i}>{line}</div>;
        }

        if (Array.isArray(line)) {
          return (
            <div key={i}>
              <LineSpans spans={line} />
            </div>
          );
        }

        if ("key" in line && "value" in line) {
          return <StatsTooltipRow key={i} label={line.key} value={<LineSpan span={line.value} />} showDollar={false} />;
        }

        return (
          <div key={i}>
            <LineSpan span={line} />
          </div>
        );
      })}
    </div>
  );
}

const PRICE_TOOLTIP_WIDTH = 400;

function TradeDetailsRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-16 border-b border-stroke-primary py-8 first:pt-0 last:border-b-0 last:pb-0">
      <span className="text-14 text-gray-lighter">{label}</span>
      <div className="max-w-[60%] break-all text-right text-14 font-medium text-white">{value}</div>
    </div>
  );
}

function TradeHistoryDetailsModal({
  chainId,
  isVisible,
  msg,
  onClose,
  tradeAction,
}: {
  chainId: number;
  isVisible: boolean;
  msg: RowDetails;
  onClose: () => void;
  tradeAction: TradeAction;
}) {
  const hasValidTransactionHash = /^0x[0-9a-fA-F]{64}$/.test(tradeAction.transaction.hash ?? "");
  const parseTxNetwork = hasValidTransactionHash ? NETWORKS_BY_CHAIN_IDS[chainId] : undefined;
  const parseTxPath = parseTxNetwork ? `/parsetx/${parseTxNetwork}/${tradeAction.transaction.hash}` : undefined;
  const explorerTxUrl = hasValidTransactionHash
    ? `${getExplorerUrl(chainId)}tx/${tradeAction.transaction.hash}`
    : undefined;

  return (
    <Modal
      isVisible={isVisible}
      setIsVisible={onClose}
      label={t`Trade Details`}
      description={t`Read-only details for the selected trade.`}
      qa="trade-history-trade-details-modal"
      contentClassName="!w-[min(96vw,720px)] !max-w-[720px]"
    >
      <div className="flex flex-col gap-20">
        <div className="rounded-8 bg-[#111111] p-16">
          <div className="mb-16 text-14 font-medium uppercase tracking-wide text-gray-lighter">
            <Trans>Overview</Trans>
          </div>
          <div className="flex flex-col">
            <TradeDetailsRow label={t`Type`} value={msg.type ?? "-"} />
            <TradeDetailsRow label={t`Action`} value={msg.action} />
            <TradeDetailsRow label={t`Time`} value={<time dateTime={msg.timestampISO}>{msg.timestamp}</time>} />
            <TradeDetailsRow label={t`Market`} value={msg.fullMarket ?? msg.market} />
            <TradeDetailsRow label={t`Size`} value={msg.size} />
            <TradeDetailsRow label={t`Price`} value={msg.price} />
            <TradeDetailsRow label={t`RPnL`} value={msg.pnl ?? "--"} />
            {tradeAction.account && tradeAction.account !== "null" && (
              <TradeDetailsRow
                label={t`Account`}
                value={
                  <Link
                    className="text-white underline hover:text-white"
                    to={buildAccountDashboardUrl(
                      (tradeAction.account.startsWith("0x") ? getAddress(tradeAction.account) : zeroAddress) as Address,
                      undefined,
                      2
                    )}
                  >
                    {tradeAction.account}
                  </Link>
                }
              />
            )}
          </div>
        </div>

        <div className="rounded-8 bg-[#111111] p-16">
          <div className="mb-16 text-14 font-medium uppercase tracking-wide text-gray-lighter">
            <Trans>Execution Data</Trans>
          </div>
          <div className="flex flex-col">
            {msg.marketPrice && <TradeDetailsRow label={t`Market Price`} value={msg.marketPrice} />}
            {msg.executionPrice && <TradeDetailsRow label={t`Execution Price`} value={msg.executionPrice} />}
            {msg.acceptablePrice && <TradeDetailsRow label={t`Acceptable Price`} value={msg.acceptablePrice} />}
            {msg.triggerPrice && <TradeDetailsRow label={t`Trigger Price`} value={msg.triggerPrice} />}
            {msg.priceImpact && <TradeDetailsRow label={t`Price Impact`} value={msg.priceImpact} />}
            <TradeDetailsRow
              label={t`Transaction`}
              value={
                explorerTxUrl ? (
                  <div className="flex flex-col items-end gap-6">
                    <ExternalLink href={explorerTxUrl}>{tradeAction.transaction.hash}</ExternalLink>
                    {parseTxPath && (
                      <Link className="text-gray-lighter underline hover:text-white" to={parseTxPath}>
                        <Trans>View parsed transaction</Trans>
                      </Link>
                    )}
                  </div>
                ) : (
                  "--"
                )
              }
            />
          </div>
        </div>

        {msg.actionComment && msg.actionComment.length > 0 && (
          <div className="rounded-8 bg-[#111111] p-16">
            <div className="mb-12 text-14 font-medium uppercase tracking-wide text-gray-lighter">
              <Trans>Action Notes</Trans>
            </div>
            <TooltipContentComponent content={msg.actionComment} />
          </div>
        )}

        {msg.priceComment && msg.priceComment.length > 0 && (
          <div className="rounded-8 bg-[#111111] p-16">
            <div className="mb-12 text-14 font-medium uppercase tracking-wide text-gray-lighter">
              <Trans>Price Notes</Trans>
            </div>
            <TooltipContentComponent content={msg.priceComment} />
          </div>
        )}

        {msg.pnlComment && msg.pnlComment.length > 0 && (
          <div className="rounded-8 bg-[#111111] p-16">
            <div className="mb-12 text-14 font-medium uppercase tracking-wide text-gray-lighter">
              <Trans>RPnL Notes</Trans>
            </div>
            <TooltipContentComponent content={msg.pnlComment} />
          </div>
        )}
      </div>
    </Modal>
  );
}

export function TradeHistoryRow({ minCollateralUsd, tradeAction, shouldDisplayAccount, showDebugValues }: Props) {
  const chainId = useSelector(selectChainId);
  const account = useSelector(selectAccount);
  const marketsInfoData = useMarketsInfoData();
  const [isTradeDetailsVisible, setIsTradeDetailsVisible] = useState(false);
  const [isPositionShareModalOpen, setIsPositionShareModalOpen] = useState(false);

  const msg = useMemo(() => {
    if (isSwapOrderType(tradeAction.orderType!)) {
      return formatSwapMessage(tradeAction as SwapTradeAction, marketsInfoData);
    }

    return formatPositionMessage(tradeAction as PositionTradeAction, minCollateralUsd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSwapOrderType(tradeAction.orderType!) && marketsInfoData, minCollateralUsd.toString(), tradeAction.id]);

  const positionTradeAction = useMemo(
    () => (isSwapOrderType(tradeAction.orderType!) ? undefined : (tradeAction as PositionTradeAction)),
    [tradeAction]
  );

  const positionShareData = useMemo(() => {
    if (!positionTradeAction) {
      return undefined;
    }

    const collateralPrice =
      positionTradeAction.collateralTokenPriceMin ??
      positionTradeAction.collateralTokenPriceMax ??
      positionTradeAction.initialCollateralToken.prices.minPrice ??
      positionTradeAction.initialCollateralToken.prices.maxPrice;
    const collateralUsd = convertToUsd(
      positionTradeAction.initialCollateralDeltaAmount,
      positionTradeAction.initialCollateralToken.decimals,
      collateralPrice
    );
    const pnlAfterFeesUsd = positionTradeAction.pnlUsd;
    let pnlAfterFeesPercentage = 0n;
    let leverage: bigint | undefined;

    if (collateralUsd !== undefined && collateralUsd !== 0n) {
      if (pnlAfterFeesUsd !== undefined) {
        pnlAfterFeesPercentage = getBasisPoints(pnlAfterFeesUsd, collateralUsd);
      }

      leverage = getBasisPoints(positionTradeAction.sizeDeltaUsd, collateralUsd);
    }

    return {
      entryPrice: positionTradeAction.executionPrice ?? positionTradeAction.acceptablePrice,
      indexToken: positionTradeAction.indexToken,
      isLong: positionTradeAction.isLong,
      leverage,
      markPrice:
        positionTradeAction.executionPrice ??
        positionTradeAction.indexTokenPriceMin ??
        positionTradeAction.indexTokenPriceMax ??
        positionTradeAction.acceptablePrice,
      pnlAfterFeesPercentage,
      pnlAfterFeesUsd,
    };
  }, [positionTradeAction]);

  const renderTimestamp = useCallback(() => msg.timestampISO, [msg.timestampISO]);

  const renderMarketContent = useCallback(() => {
    if (msg.indexName) {
      return (
        <StatsTooltipRow
          label={t`Market`}
          value={
            <div className="flex items-center">
              <span>{msg.indexName!}</span>
              <span className="subtext leading-1">[{msg.poolName!}]</span>
            </div>
          }
          showDollar={false}
        />
      );
    }

    return (
      <>
        {msg.fullMarketNames?.map((market, index) => (
          <span key={market.indexName}>
            {index > 0 && " → "}
            <span>{market.indexName}</span>
            <span className="subtext leading-1">[{market.poolName}]</span>
          </span>
        ))}
      </>
    );
  }, [msg.fullMarketNames, msg.indexName, msg.poolName]);

  const renderPriceContent = useCallback(
    () => <TooltipContentComponent content={msg.priceComment ?? EMPTY_ARRAY} />,
    [msg.priceComment]
  );

  const renderActionTooltipContent = useCallback(
    () => <TooltipContentComponent content={msg.actionComment!} />,
    [msg.actionComment]
  );

  const marketTooltipHandle = useMemo(() => {
    if (msg.swapFromTokenSymbol) {
      return <SwapMarketLabel bordered fromSymbol={msg.swapFromTokenSymbol!} toSymbol={msg.swapToTokenSymbol!} />;
    }

    if (msg.isLong !== undefined) {
      const direction = msg.isLong ? t`Long` : t`Short`;
      const poolLabel = msg.poolName ? msg.poolName.split("-").join("/") : msg.market.replace(/^\S+\s+/, "");

      return (
        <div className="cursor-help">
          <span className={cx(msg.isLong ? "text-primary-alpha-darker" : "text-red-alpha-100")}>{direction}</span>
          <span> {poolLabel}</span>
        </div>
      );
    }

    return <div className="cursor-help">{msg.market}</div>;
  }, [msg.isLong, msg.market, msg.poolName, msg.swapFromTokenSymbol, msg.swapToTokenSymbol]);

  const hasValidTransactionHash = /^0x[0-9a-fA-F]{64}$/.test(tradeAction.transaction.hash ?? "");
  const handleShare = useCallback(() => {
    userAnalytics.pushEvent<SharePositionClickEvent>({
      event: "SharePositionAction",
      data: {
        action: "SharePositionClick",
      },
    });
    setIsPositionShareModalOpen(true);
  }, []);

  return (
    <>
      <TableTr
        className={cx("TradeHistoryRow !border-none", {
          debug: showDebugValues,
        })}
      >
        <TableTd>
          <span className="rounded-4 bg-gray-light px-6 py-2 text-12 font-medium uppercase text-white">{msg.type}</span>
        </TableTd>
        <TableTd>
          <div className="flex">
            {msg.actionComment ? (
              <TooltipWithPortal
                className={cx("TradeHistoryRow-action-handle")}
                handleClassName={cx("TradeHistoryRow-action-handle", {
                  "text-red-alpha-100": msg.isActionError,
                })}
                handle={msg.action}
                renderContent={renderActionTooltipContent}
              />
            ) : (
              <span
                className={cx("TradeHistoryRow-action-handle", {
                  "text-red-alpha-100": msg.isActionError,
                })}
              >
                {msg.action}
              </span>
            )}
            <div className="flex flex-row items-center">
              {hasValidTransactionHash && (
                <ExternalLink
                  className="TradeHistoryRow-external-link ml-5"
                  href={`${getExplorerUrl(chainId)}tx/${tradeAction.transaction.hash}`}
                >
                  <NewLink20ReactComponent />
                </ExternalLink>
              )}
              {showDebugValues && (
                <Link
                  to={`/parsetx/${NETWORKS_BY_CHAIN_IDS[chainId]}/${tradeAction.transaction.hash}`}
                  className="text-body-small ml-5 text-gray-lighter hover:text-white"
                >
                  Events
                </Link>
              )}
            </div>
          </div>
          <TooltipWithPortal
            disableHandleStyle
            handle={<span className="TradeHistoryRow-time muted cursor-help">{msg.timestamp}</span>}
            tooltipClassName="TradeHistoryRow-tooltip-portal cursor-help *:cursor-auto"
            renderContent={renderTimestamp}
          />
          {shouldDisplayAccount && tradeAction.account && tradeAction.account !== "null" && (
            <Link
              className="TradeHistoryRow-account muted underline"
              to={buildAccountDashboardUrl(
                (tradeAction.account.startsWith("0x") ? getAddress(tradeAction.account) : zeroAddress) as Address,
                undefined,
                2
              )}
            >
              {tradeAction.account}
            </Link>
          )}
        </TableTd>
        <TableTd>
          <TooltipWithPortal
            disableHandleStyle
            tooltipClassName="cursor-help *:cursor-auto"
            handle={marketTooltipHandle}
            renderContent={renderMarketContent}
          />
        </TableTd>
        <TableTd>
          {msg.swapFromTokenSymbol ? (
            <Trans>
              {msg.swapFromTokenAmount} <TokenIcon symbol={msg.swapFromTokenSymbol!} displaySize={18} importSize={24} />
              <span> to </span>
              {msg.swapToTokenAmount} <TokenIcon symbol={msg.swapToTokenSymbol!} displaySize={18} importSize={24} />
            </Trans>
          ) : (
            msg.size
          )}
        </TableTd>
        <TableTd>
          {msg.priceComment ? (
            <TooltipWithPortal
              tooltipClassName="TradeHistoryRow-price-tooltip-portal"
              handle={msg.price}
              position="bottom-end"
              renderContent={renderPriceContent}
              maxAllowedWidth={PRICE_TOOLTIP_WIDTH}
            />
          ) : (
            <>{msg.price}</>
          )}
        </TableTd>
        <TableTd className="TradeHistoryRow-pnl-fees">
          {msg.pnlComment ? (
            <TooltipWithPortal
              tooltipClassName="TradeHistoryRow-price-tooltip-portal"
              handle={<span className="cursor-help text-gray-lighter">{msg.pnl ?? "--"}</span>}
              position="bottom-end"
              renderContent={() => <TooltipContentComponent content={msg.pnlComment ?? EMPTY_ARRAY} />}
              maxAllowedWidth={PRICE_TOOLTIP_WIDTH}
            />
          ) : msg.pnl ? (
            <span
              className={cx({
                "text-red-alpha-100": msg.pnlState === "error",
                "text-primary-alpha-darker": msg.pnlState === "success",
              })}
            >
              {msg.pnl}
            </span>
          ) : (
            <span className="text-gray-lighter">--</span>
          )}
        </TableTd>
        <TableTd>
          <PositionDropdown
            handleShare={positionShareData ? handleShare : undefined}
            handleTradeDetails={() => setIsTradeDetailsVisible(true)}
          />
        </TableTd>
      </TableTr>
      {showDebugValues && (
        <TableTr>
          <TableTd colSpan={42}>
            <div className="muted">Order Key: {tradeAction.orderKey}</div>
          </TableTd>
        </TableTr>
      )}
      <TradeHistoryDetailsModal
        chainId={chainId}
        isVisible={isTradeDetailsVisible}
        msg={msg}
        onClose={() => setIsTradeDetailsVisible(false)}
        tradeAction={tradeAction}
      />
      {positionShareData && (
        <PositionShare
          key={tradeAction.id}
          setIsPositionShareModalOpen={setIsPositionShareModalOpen}
          isPositionShareModalOpen={isPositionShareModalOpen}
          entryPrice={positionShareData.entryPrice}
          indexToken={positionShareData.indexToken}
          isLong={positionShareData.isLong}
          leverage={positionShareData.leverage}
          markPrice={positionShareData.markPrice}
          pnlAfterFeesUsd={positionShareData.pnlAfterFeesUsd}
          pnlAfterFeesPercentage={positionShareData.pnlAfterFeesPercentage}
          chainId={chainId}
          account={account}
        />
      )}
    </>
  );
}
